import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import DateInput from "@/components/DateInput";
import { CalendarClock, Pencil, Plus, Trash2, X, Users, ChevronLeft, ChevronRight, UserPlus, Calendar as CalendarIcon } from "lucide-react";
import { useConfirm } from "@/components/ConfirmProvider";

const ESTADOS = [
  { value: "SOLICITADA", label: "Solicitada", chip: "bg-slate-100 text-slate-700" },
  { value: "EN_TRAMITE", label: "En trámite", chip: "bg-amber-50 text-amber-700" },
  { value: "AUTORIZADA", label: "Autorizada", chip: "bg-emerald-50 text-emerald-700" },
  { value: "RECHAZADA", label: "Rechazada", chip: "bg-red-50 text-red-700" },
];
const estadoLabel = (v) => (ESTADOS.find((e) => e.value === v) || ESTADOS[0]).label;
const estadoChip = (v) => (ESTADOS.find((e) => e.value === v) || ESTADOS[0]).chip;

const fmt = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}-${m}-${y}` : s;
};

export default function JornadasSection({ mid, cid, canEdit, cargos: cargosProp, refreshKey }) {
  const confirmAction = useConfirm();
  const [list, setList] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [contratoWorkers, setContratoWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [calendarFor, setCalendarFor] = useState(null);

  // Si el padre pasa cargos, los usamos como fuente de verdad.
  useEffect(() => {
    if (Array.isArray(cargosProp)) {
      setCargos(cargosProp.map((x) => (typeof x === "string" ? x : x.name)));
    }
  }, [cargosProp]);

  const reload = async () => {
    setLoading(true);
    try {
      const [j, cg, m] = await Promise.all([
        api.get(`/mandantes/${mid}/contratos/${cid}/jornadas`),
        api.get(`/mandantes/${mid}/contratos/${cid}/cargos`),
        api.get(`/mandantes/${mid}/contratos/${cid}`),
      ]);
      setList(j.data);
      if (!Array.isArray(cargosProp)) setCargos((cg.data || []).map((x) => x.name));
      // Trabajadores del contrato
      const mand = (await api.get("/mandantes")).data.find((x) => x.id === mid);
      const workers = (await api.get("/workers")).data.filter(
        (w) => w.mandante === mand?.name && w.contrato === m.data?.name
      );
      setContratoWorkers(workers);
    } catch (e) { toast.error("No se pudieron cargar las jornadas"); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, [mid, cid, refreshKey]);

  const remove = async (j) => {
    const ok = await confirmAction({ title: "Eliminar jornada excepcional",
      description: `¿Eliminar la jornada con resolución "${j.numero_resolucion || "sin número"}"? Esta acción no se puede deshacer.`,
      confirmText: "Eliminar", variant: "danger" });
    if (!ok) return;
    try {
      await api.delete(`/mandantes/${mid}/contratos/${cid}/jornadas/${j.id}`);
      setList((prev) => prev.filter((x) => x.id !== j.id));
      toast.success("Jornada eliminada");
    } catch (e) { toast.error("No se pudo eliminar"); }
  };

  const workerName = (id) => {
    const w = contratoWorkers.find((x) => x.id === id);
    return w ? `${w.first_name} ${w.last_name}` : "—";
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2">
            <CalendarClock size={18} className="text-indigo-600" /> Jornadas excepcionales
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">Define el ciclo (días de trabajo × descanso), asigna trabajadores y revisa el calendario mensual.</p>
        </div>
        {canEdit && (
          <button data-testid="add-jornada-btn" onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md">
            <Plus size={13} /> Nueva jornada
          </button>
        )}
      </div>
      {loading ? (
        <div className="p-6 text-sm text-slate-400">Cargando…</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {list.map((j) => (
            <JornadaCard
              key={j.id}
              j={j}
              canEdit={canEdit}
              workerName={workerName}
              onEdit={() => setEditing(j)}
              onDelete={() => remove(j)}
              onAssign={() => setAssigning(j)}
              onCalendar={() => setCalendarFor(j)}
              onUnassign={async (wid) => {
                try {
                  const { data } = await api.delete(`/mandantes/${mid}/contratos/${cid}/jornadas/${j.id}/trabajadores/${wid}`);
                  setList((prev) => prev.map((x) => (x.id === j.id ? data : x)));
                  toast.success("Trabajador desasignado");
                } catch (e) { toast.error("No se pudo desasignar"); }
              }}
            />
          ))}
          {list.length === 0 && (
            <div className="px-5 py-10 text-sm text-slate-400 italic text-center" data-testid="jornadas-empty">
              Sin jornadas excepcionales registradas.
            </div>
          )}
        </div>
      )}

      {creating && (
        <JornadaDialog cargos={cargos} onClose={() => setCreating(false)}
          onSubmit={async (payload) => {
            try {
              const { data } = await api.post(`/mandantes/${mid}/contratos/${cid}/jornadas`, payload);
              setList((prev) => [...prev, data].sort((a,b) => (a.fecha_inicio||"").localeCompare(b.fecha_inicio||"")));
              toast.success("Jornada creada"); setCreating(false);
            } catch (e) { toast.error(e.response?.data?.detail || "No se pudo crear"); }
          }} />
      )}
      {editing && (
        <JornadaDialog cargos={cargos} initial={editing} onClose={() => setEditing(null)}
          onSubmit={async (payload) => {
            try {
              const { data } = await api.put(`/mandantes/${mid}/contratos/${cid}/jornadas/${editing.id}`, payload);
              setList((prev) => prev.map((x) => (x.id === data.id ? data : x)));
              toast.success("Jornada actualizada"); setEditing(null);
            } catch (e) { toast.error(e.response?.data?.detail || "No se pudo actualizar"); }
          }} />
      )}
      {assigning && (
        <AssignWorkersDialog
          jornada={assigning}
          workers={contratoWorkers}
          onClose={() => setAssigning(null)}
          onSave={async (worker_ids, fecha_ingreso_ciclo) => {
            try {
              const { data } = await api.post(
                `/mandantes/${mid}/contratos/${cid}/jornadas/${assigning.id}/trabajadores`,
                { worker_ids, fecha_ingreso_ciclo }
              );
              setList((prev) => prev.map((x) => (x.id === data.id ? data : x)));
              toast.success("Trabajadores asignados"); setAssigning(null);
            } catch (e) { toast.error(e.response?.data?.detail || "No se pudo asignar"); }
          }}
          onEditFecha={async (wid, fecha) => {
            try {
              const { data } = await api.patch(
                `/mandantes/${mid}/contratos/${cid}/jornadas/${assigning.id}/trabajadores/${wid}`,
                { fecha_ingreso_ciclo: fecha }
              );
              setList((prev) => prev.map((x) => (x.id === data.id ? data : x)));
              setAssigning(data);
              toast.success("Fecha actualizada");
            } catch (e) { toast.error("No se pudo actualizar"); }
          }}
          onRemove={async (wid) => {
            try {
              const { data } = await api.delete(
                `/mandantes/${mid}/contratos/${cid}/jornadas/${assigning.id}/trabajadores/${wid}`
              );
              setList((prev) => prev.map((x) => (x.id === data.id ? data : x)));
              setAssigning(data);
              toast.success("Trabajador desasignado");
            } catch (e) { toast.error("No se pudo desasignar"); }
          }}
          canEdit={canEdit}
        />
      )}
      {calendarFor && (
        <CalendarDialog mid={mid} cid={cid} jornada={calendarFor} onClose={() => setCalendarFor(null)} />
      )}
    </section>
  );
}

/* ------------------------------- Card ------------------------------- */
function JornadaCard({ j, canEdit, workerName, onEdit, onDelete, onAssign, onCalendar, onUnassign }) {
  const dt = Number(j.dias_trabajo || 0);
  const dd = Number(j.dias_descanso || 0);
  const cycle = dt + dd;
  const asignados = j.trabajadores || [];
  return (
    <div data-testid={`jornada-row-${j.id}`} className="px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-slate-800">
              Res. {j.numero_resolucion || "s/n"}
            </span>
            <span className={`inline-flex items-center h-6 px-2 rounded text-xs font-medium ${estadoChip(j.estado)}`}>
              {estadoLabel(j.estado)}
            </span>
            {cycle > 0 && (
              <span className="inline-flex items-center h-6 px-2 rounded text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                {dt} × {dd}
              </span>
            )}
            {j.fecha_inicio_ciclo && (
              <span className="text-xs text-slate-500">Inicio ciclo: <strong>{fmt(j.fecha_inicio_ciclo)}</strong></span>
            )}
          </div>
          <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
            <span>Vigencia: {fmt(j.fecha_inicio)} → {fmt(j.fecha_termino)}</span>
            {(j.cargos || []).length > 0 && (
              <span className="flex items-center gap-1 flex-wrap">
                Cargos:
                {(j.cargos || []).map((c) => (
                  <span key={c} className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{c}</span>
                ))}
              </span>
            )}
          </div>

          {cycle > 0 && <CyclePreview dt={dt} dd={dd} />}

          {asignados.length > 0 && (
            <div className="mt-3">
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1">
                Trabajadores asignados ({asignados.length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {asignados.map((t) => (
                  <span key={t.worker_id} className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-md px-2 py-0.5 text-xs text-slate-700">
                    <Users size={11} className="text-slate-400" />
                    {workerName(t.worker_id)}
                    {t.fecha_ingreso_ciclo && (
                      <span className="text-[10px] text-slate-400">· {fmt(t.fecha_ingreso_ciclo)}</span>
                    )}
                    {canEdit && (
                      <button
                        onClick={() => onUnassign(t.worker_id)}
                        className="text-slate-400 hover:text-red-500 ml-0.5"
                        title="Desasignar"
                        data-testid={`quick-unassign-${t.worker_id}`}
                      ><X size={11} /></button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <button
            data-testid={`calendar-jornada-${j.id}`}
            onClick={onCalendar}
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100">
            <CalendarIcon size={13} /> Calendario
          </button>
          {canEdit && (
            <>
              <button
                data-testid={`assign-jornada-${j.id}`}
                onClick={onAssign}
                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-100">
                <UserPlus size={13} /> Asignar
              </button>
              <button data-testid={`edit-jornada-${j.id}`} onClick={onEdit}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:text-indigo-600 hover:bg-slate-50">
                <Pencil size={14} />
              </button>
              <button data-testid={`del-jornada-${j.id}`} onClick={onDelete}
                className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:text-red-600 hover:bg-slate-50">
                <Trash2 size={14} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Cycle preview ----------------------------- */
function CyclePreview({ dt, dd }) {
  const cycle = dt + dd;
  if (!cycle) return null;
  const total = Math.min(cycle, 28);
  return (
    <div className="mt-2 flex flex-wrap gap-0.5" data-testid="cycle-preview">
      {Array.from({ length: total }).map((_, i) => {
        const trabajo = i < dt;
        return (
          <span
            key={i}
            className={`h-5 w-5 rounded text-[9px] inline-flex items-center justify-center font-semibold ${
              trabajo ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
            }`}
            title={trabajo ? "Trabajo" : "Descanso"}
          >{i + 1}</span>
        );
      })}
    </div>
  );
}

/* --------------------------- Create / edit dialog --------------------------- */
function JornadaDialog({ cargos, initial, onClose, onSubmit }) {
  const [form, setForm] = useState({
    numero_resolucion: initial?.numero_resolucion || "",
    fecha_inicio: initial?.fecha_inicio || "",
    fecha_termino: initial?.fecha_termino || "",
    cargos: initial?.cargos || [],
    estado: initial?.estado || "SOLICITADA",
    dias_trabajo: initial?.dias_trabajo ?? 7,
    dias_descanso: initial?.dias_descanso ?? 7,
    fecha_inicio_ciclo: initial?.fecha_inicio_ciclo || "",
  });
  const [saving, setSaving] = useState(false);
  const availableCargos = useMemo(() => {
    const base = new Set(cargos || []);
    (initial?.cargos || []).forEach((c) => base.add(c));
    return Array.from(base).sort();
  }, [cargos, initial]);
  const toggleCargo = (c) => setForm((f) => ({
    ...f, cargos: f.cargos.includes(c) ? f.cargos.filter((x) => x !== c) : [...f.cargos, c],
  }));
  const save = async () => { setSaving(true); try { await onSubmit({ ...form, dias_trabajo: Number(form.dias_trabajo || 0), dias_descanso: Number(form.dias_descanso || 0) }); } finally { setSaving(false); } };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2">
            <CalendarClock size={18} className="text-indigo-600" />
            {initial ? "Editar jornada" : "Nueva jornada excepcional"}
          </h3>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="N° Resolución">
            <input data-testid="jornada-resolucion"
              value={form.numero_resolucion} onChange={(e) => setForm({ ...form, numero_resolucion: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha de inicio">
              <DateInput testid="jornada-inicio"
                value={form.fecha_inicio || ""}
                onChange={(iso) => setForm({ ...form, fecha_inicio: iso })} />
            </Field>
            <Field label="Fecha de término">
              <DateInput testid="jornada-termino"
                value={form.fecha_termino || ""}
                onChange={(iso) => setForm({ ...form, fecha_termino: iso })} />
            </Field>
          </div>

          <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-2">
            <p className="text-xs font-semibold text-indigo-700">Ciclo de turnos</p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Días de trabajo">
                <input data-testid="jornada-dias-trabajo" type="number" min={0}
                  value={form.dias_trabajo}
                  onChange={(e) => setForm({ ...form, dias_trabajo: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" />
              </Field>
              <Field label="Días de descanso">
                <input data-testid="jornada-dias-descanso" type="number" min={0}
                  value={form.dias_descanso}
                  onChange={(e) => setForm({ ...form, dias_descanso: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" />
              </Field>
              <Field label="Parte el">
                <DateInput testid="jornada-fecha-inicio-ciclo"
                  value={form.fecha_inicio_ciclo || ""}
                  onChange={(iso) => setForm({ ...form, fecha_inicio_ciclo: iso })} />
              </Field>
            </div>
            <CyclePreview dt={Number(form.dias_trabajo || 0)} dd={Number(form.dias_descanso || 0)} />
            <p className="text-[11px] text-slate-500">
              Ej: 7×7 significa 7 días de trabajo seguidos y 7 de descanso. El patrón se repite desde la fecha "Parte el".
            </p>
          </div>

          <Field label="Estado">
            <select data-testid="jornada-estado" value={form.estado}
              onChange={(e) => setForm({ ...form, estado: e.target.value })}
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              {ESTADOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </Field>
          <Field label={`Cargos asociados (opcional) (${availableCargos.length === 0 ? "sin trabajadores aún" : `${availableCargos.length} disponibles`})`}>
            {availableCargos.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Puedes dejarlo vacío y asignar trabajadores directamente desde el botón "Asignar".</p>
            ) : (
              <div data-testid="jornada-cargos" className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border border-slate-200 rounded-lg bg-slate-50">
                {availableCargos.map((c) => {
                  const checked = form.cargos.includes(c);
                  return (
                    <button type="button" key={c} data-testid={`cargo-${c}`}
                      onClick={() => toggleCargo(c)}
                      className={`inline-flex items-center h-7 px-2.5 rounded-md text-xs font-medium transition-colors ${
                        checked ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-indigo-300"
                      }`}>{c}</button>
                  );
                })}
              </div>
            )}
          </Field>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-100">
          <button onClick={onClose} className="h-10 px-4 rounded-lg text-sm font-medium border border-slate-200 bg-white hover:bg-slate-50">Cancelar</button>
          <button data-testid="jornada-save" onClick={save} disabled={saving}
            className="h-10 px-4 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
            {saving ? "Guardando…" : (initial ? "Guardar cambios" : "Crear jornada")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- Assign workers ---------------------------- */
function AssignWorkersDialog({ jornada, workers, onClose, onSave, onEditFecha, onRemove, canEdit }) {
  const asignadosIds = (jornada.trabajadores || []).map((t) => t.worker_id);
  const [selected, setSelected] = useState([]);
  const [fecha, setFecha] = useState("");
  const [query, setQuery] = useState("");

  const disponibles = useMemo(() => {
    const q = query.trim().toLowerCase();
    return workers.filter((w) => !asignadosIds.includes(w.id))
      .filter((w) => !q || (`${w.first_name} ${w.last_name} ${w.rut || ""}`.toLowerCase().includes(q)));
  }, [workers, asignadosIds, query]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const save = async () => { if (selected.length === 0) return; await onSave(selected, fecha); setSelected([]); setFecha(""); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2">
              <UserPlus size={18} className="text-emerald-600" />
              Asignar trabajadores · Res. {jornada.numero_resolucion || "s/n"}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Ciclo {Number(jornada.dias_trabajo || 0)}×{Number(jornada.dias_descanso || 0)} desde {fmt(jornada.fecha_inicio_ciclo)}
            </p>
          </div>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 divide-x divide-slate-100 overflow-hidden">
          {/* ya asignados */}
          <div className="p-4 overflow-y-auto max-h-[60vh]">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Ya asignados ({(jornada.trabajadores || []).length})</p>
            {(jornada.trabajadores || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic">Nadie asignado aún.</p>
            ) : (
              <div className="space-y-2">
                {(jornada.trabajadores || []).map((t) => {
                  const w = workers.find((x) => x.id === t.worker_id);
                  return (
                    <div key={t.worker_id} data-testid={`assigned-worker-${t.worker_id}`}
                      className="p-2.5 rounded-lg border border-slate-200 bg-slate-50">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">
                            {w ? `${w.first_name} ${w.last_name}` : t.worker_id}
                          </p>
                          <p className="text-xs text-slate-500">{w?.rut || "—"} · {w?.position || "—"}</p>
                        </div>
                        {canEdit && (
                          <button onClick={() => onRemove(t.worker_id)}
                            data-testid={`remove-worker-${t.worker_id}`}
                            className="text-slate-400 hover:text-red-600 h-7 w-7 inline-flex items-center justify-center rounded">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <label className="text-[11px] text-slate-500">Entra al ciclo:</label>
                        <div className="flex-1">
                          <DateInput testid={`worker-fecha-${t.worker_id}`}
                            value={t.fecha_ingreso_ciclo || ""}
                            onChange={(iso) => onEditFecha(t.worker_id, iso)}
                            disabled={!canEdit} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* disponibles */}
          <div className="p-4 overflow-y-auto max-h-[60vh]">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Disponibles ({disponibles.length})</p>
            <input placeholder="Buscar por nombre o RUT…"
              data-testid="assign-search"
              value={query} onChange={(e) => setQuery(e.target.value)}
              className="w-full h-9 px-3 mb-2 rounded-lg border border-slate-200 text-sm" />
            <div className="space-y-1">
              {disponibles.map((w) => {
                const checked = selected.includes(w.id);
                return (
                  <button key={w.id} type="button"
                    data-testid={`avail-worker-${w.id}`}
                    onClick={() => toggle(w.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors flex items-center justify-between ${
                      checked ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200 hover:border-emerald-200"
                    }`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{w.first_name} {w.last_name}</p>
                      <p className="text-xs text-slate-500">{w.rut} · {w.position || "—"}</p>
                    </div>
                    {checked && <span className="text-xs font-semibold text-emerald-700">✓</span>}
                  </button>
                );
              })}
              {disponibles.length === 0 && <p className="text-xs text-slate-400 italic">Sin trabajadores disponibles.</p>}
            </div>
          </div>
        </div>

        {canEdit && (
          <div className="border-t border-slate-100 p-4 flex flex-col md:flex-row md:items-center md:justify-end gap-3 bg-slate-50">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-slate-600 whitespace-nowrap">Entran al ciclo el:</label>
              <div className="w-40">
                <DateInput testid="assign-fecha-ingreso"
                  value={fecha} onChange={setFecha} />
              </div>
            </div>
            <button onClick={onClose} className="h-9 px-4 rounded-lg text-sm font-medium border border-slate-200 bg-white hover:bg-slate-100">Cerrar</button>
            <button onClick={save} disabled={selected.length === 0}
              data-testid="assign-submit"
              className="h-9 px-4 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
              Asignar {selected.length > 0 ? `(${selected.length})` : ""}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- Calendar ------------------------------- */
function CalendarDialog({ mid, cid, jornada, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const [year, setYear] = useState(today.getUTCFullYear());
  const [month, setMonth] = useState(today.getUTCMonth() + 1);

  const load = async () => {
    setLoading(true);
    try {
      const mm = `${year}-${String(month).padStart(2, "0")}`;
      const { data } = await api.get(
        `/mandantes/${mid}/contratos/${cid}/jornadas/${jornada.id}/calendario`,
        { params: { month: mm } }
      );
      setData(data);
    } catch (e) { toast.error("No se pudo cargar el calendario"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const next = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  const prev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };

  const dayLabel = (iso) => iso.slice(8, 10);
  const monthName = new Date(year, month - 1, 1).toLocaleString("es-CL", { month: "long", year: "numeric" });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2">
              <CalendarIcon size={18} className="text-indigo-600" />
              Calendario · Res. {jornada.numero_resolucion || "s/n"}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Ciclo {Number(jornada.dias_trabajo || 0)}×{Number(jornada.dias_descanso || 0)} desde {fmt(jornada.fecha_inicio_ciclo)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={prev} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-50"><ChevronLeft size={16} /></button>
            <span className="min-w-[150px] text-center text-sm font-semibold capitalize" data-testid="cal-month-label">{monthName}</span>
            <button onClick={next} className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-50"><ChevronRight size={16} /></button>
            <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500 ml-2"><X size={16} /></button>
          </div>
        </div>

        <div className="overflow-auto flex-1">
          {loading || !data ? (
            <div className="p-10 text-sm text-slate-400">Cargando…</div>
          ) : (
            <div className="min-w-max">
              <table className="text-xs" data-testid="jornada-calendar">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-white z-10 px-3 py-2 text-left font-semibold text-slate-600 border-b border-r border-slate-200 min-w-[220px]">Trabajador</th>
                    {data.days.map((d) => (
                      <th key={d} className="px-1.5 py-2 text-center font-medium text-slate-500 border-b border-slate-100">
                        <div className="text-[10px] text-slate-400 uppercase">{new Date(d + "T00:00:00").toLocaleDateString("es-CL", { weekday: "short" }).slice(0,2)}</div>
                        <div>{dayLabel(d)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.workers.length === 0 && (
                    <tr>
                      <td colSpan={data.days.length + 1} className="p-6 text-sm text-slate-400 italic text-center">
                        Sin trabajadores asignados.
                      </td>
                    </tr>
                  )}
                  {data.workers.map((w) => (
                    <tr key={w.worker_id} data-testid={`cal-row-${w.worker_id}`}>
                      <td className="sticky left-0 bg-white z-10 px-3 py-2 border-b border-r border-slate-100 whitespace-nowrap">
                        <div className="font-medium text-slate-800">{w.nombre}</div>
                        <div className="text-[10px] text-slate-400">{w.rut || "—"}</div>
                      </td>
                      {w.dias.map((s, i) => {
                        const cls = s === "trabajo" ? "bg-emerald-500 text-white"
                                  : s === "descanso" ? "bg-slate-200 text-slate-500"
                                  : s === "antes" ? "bg-amber-50 text-amber-500 border border-dashed border-amber-200"
                                  : "bg-slate-50 text-slate-300";
                        const letter = s === "trabajo" ? "T" : s === "descanso" ? "D" : s === "antes" ? "·" : "—";
                        return (
                          <td key={i} className="px-0.5 py-1 border-b border-slate-100">
                            <div className={`h-6 w-6 mx-auto rounded flex items-center justify-center text-[10px] font-semibold ${cls}`}
                              title={`${data.days[i]} · ${s}`}>
                              {letter}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-3 flex items-center gap-4 bg-slate-50">
          <div className="flex items-center gap-1.5 text-xs text-slate-600"><span className="inline-block h-3 w-3 rounded bg-emerald-500" /> Trabajo</div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600"><span className="inline-block h-3 w-3 rounded bg-slate-200" /> Descanso</div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600"><span className="inline-block h-3 w-3 rounded bg-amber-50 border border-dashed border-amber-200" /> Antes de ingresar</div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600"><span className="inline-block h-3 w-3 rounded bg-slate-50" /> Sin ciclo definido</div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
